import { StreamActions, StreamElement } from '@hotwired/turbo'

/**
 * Dispatches a custom DOM event from a Turbo Stream response.
 *
 * Attributes:
 *   - `event`  (required) — the event name to dispatch
 *   - `target` (optional) — the id of the DOM element to dispatch from; defaults to `<html>`
 *   - `detail` (optional) — JSON-serialized payload passed as `event.detail`
 *
 * @example:
 * ```rb
 * turbo_perform_actions(
 *   turbo_stream.dispatch("event-name") # Dispatches to the `<html>` element by default
 *   turbo_stream.dispatch("namespace:event-name", target: "some-element-id")
 *   turbo_stream.dispatch("namespace:event-name", target: "some-element-id", detail: {foo: "bar"})
 * )
 * ```
 */
StreamActions.dispatch = function (this: StreamElement) {
  const eventName = this.getAttribute('event')

  if (!eventName) {
    console.warn('The `dispatch` Turbo Stream action requires an `event` attribute. Skipping.')
    return
  }

  const detailAttr = this.getAttribute('detail')
  const detail = detailAttr ? JSON.parse(detailAttr) : undefined
  const target = this.target ? document.getElementById(this.target) : document.documentElement

  target?.dispatchEvent(new CustomEvent(eventName, { bubbles: true, cancelable: true, detail }))
}
